import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
    TextInput,
    Text,
    Tiles,
    Modal,
    Services,
    Button,
    BubbleSelectBlock,
    Theme
} from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import FormControl from '@mui/material/FormControl';

import { createAdmin, updateAdmin, removeAdmin, resetAdminPassword } from '../../../actions/accounts.js';
import permissionLevel from '../../../services/permissions.js';
import PermissionsComponent from '../../../components/PermissionsComponent.js';

class Administrators extends Component {
    state = {
        firstname: '',
        lastname: '',
        email: '',
        language: '',
        showAdminForm: false,
        selectedAdminID: false,
        isEdit: false,
        isParent: 0,
        isPlanSponsor: 0,
        divisionsById: {},
        resetAdminPasswordModal: false,
        useMFA: false
    };

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Administrators', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    _hideForm = () => {
        this.setState({
            showAdminForm: false
        });
    };

    _showNewAdmin = () => {
        this.setState({
            showAdminForm: true,
            email: '',
            firstname: '',
            lastname: '',
            language: '',
            isEdit: false,
            selectedAdminID: false,
            isParent: 0,
            isPlanSponsor: 0,
            divisionsById: {},
            firstnameError: false,
            lastnameError: false,
            emailError: false,
            useMFA: false
        });
    };

    _showEdit = (admin) => {
        const divisionsArr = Array.isArray(admin.divisions) ? admin.divisions : Object.values(admin.divisions);
        const divisionEntries = divisionsArr.map(({ divisionID, access }) => [ divisionID, { divisionID, access }]);

        this.setState({
            firstname: admin ? admin.firstname : '',
            lastname: admin ? admin.lastname : '',
            email: admin ? admin.email : '',
            language: admin ? admin.language === "fr" ? "French" : "English" : '',
            isEdit: true,
            showAdminForm: true,
            selectedAdminID: admin.adminID,
            isParent: admin.isParent,
            isPlanSponsor: admin.isPlanSponsor,
            divisionsById: Object.fromEntries(divisionEntries),
            firstnameError: false,
            lastnameError: false,
            emailError: false,
            useMFA: admin?.useMFA || false
        });
    };

    _onBlur = async (type) => {
        this.setState({
            [`${ type }Error`]: await Services.validateInput(type, this.state[type])
        });
    };

    _toggle = (type) => {
        this.setState({
            [type]: !this.state[type]
        });
    };

    _handleInputChange = (type, event, isValue = false) => {
        this.setState({
            [type]: isValue ? event : event.target.value
        });
    };

    /**
     * @returns {Promise<void>}
     * @private
     */
    _editAdmin = async () => {
        const firstnameError = await Services.validateInput('firstname', this.state.firstname);
        const lastnameError = await Services.validateInput('lastname', this.state.lastname);
        const emailError = await Services.validateInput('email', this.state.email);

        this.setState({
            firstnameError,
            lastnameError,
            emailError
        });

        if (
            firstnameError ||
            lastnameError ||
            emailError
        ) {
            return false;
        }

        const success = await this.props.dispatch(updateAdmin({
            firstname: this.state.firstname.trim(),
            lastname: this.state.lastname.trim(),
            email: this.state.email.trim(),
            language: this.state.language === "French" ? "fr" : "en",
            isParent: this.state.isParent || 0,
            selectedDivisions: Object.values(this.state.divisionsById),
            useMFA: Boolean(this.state.useMFA)
        }, this.state.selectedAdminID));

        if (success) {
            this._hideForm();
        }

        return null;
    };

    /**
     * @returns {Promise<void>}
     * @private
     */
    _saveAdmin = async () => {
        const firstnameError = await Services.validateInput('firstname', this.state.firstname);
        const lastnameError = await Services.validateInput('lastname', this.state.lastname);
        const emailError = await Services.validateInput('email', this.state.email);

        this.setState({
            firstnameError,
            lastnameError,
            emailError
        });

        if (
            firstnameError ||
            lastnameError ||
            emailError
        ) {
            return false;
        }

        const success = await this.props.dispatch(createAdmin({
            firstname: this.state.firstname.trim(),
            lastname: this.state.lastname.trim(),
            email: this.state.email.trim(),
            language: this.state.language === "French" ? "fr" : "en",
            isParent: this.state.isParent || 0,
            selectedDivisions: Object.values(this.state.divisionsById),
            useMFA: Boolean(this.state.useMFA)
        }, this.props.accountID));

        if (success) {
            this._hideForm();
        }

        return null;
    };

    /**
     * @private
     */
    _removeAdmin = async () => {
        // TODO: make a nicer confirm
        if (window.confirm('Are you sure?')) {
            const success = await this.props.dispatch(removeAdmin(this.state.selectedAdminID));

            if (success) {
                this._hideForm();
            }
        }
    };

    _handleDivisionSelection(divisionID, access) {
        const { divisionsById } = this.state;

        if (divisionID in divisionsById) {
            if (divisionsById[divisionID].access === access) {
                delete divisionsById[divisionID];
            } else {
                divisionsById[divisionID] = { access, divisionID };
            }
        } else {
            divisionsById[divisionID] = { access, divisionID };
        }

        this.setState({ divisionsById });

    }

    _resetAdminPassword = async () => {
        const success = await this.props.dispatch(resetAdminPassword({ email: this.state.email }));

        if (success) {
            this._toggle('resetAdminPasswordModal');
        }
    };

    _getTiles = () => {
        const { administrators } = this.props;
        const planSponsorID = Object.keys(administrators).find((a) => administrators[a].isPlanSponsor === 1);
        const planSponsor = administrators[planSponsorID];

        const statusLabel = {
            1: { label: 'Active', color: Theme.colors.brandGreen },
            2: { label: 'Disabled', color: Theme.colors.darkGrey },
            9: { label: 'Terminated', color: Theme.colors.darkGrey }
        };

        let tiles = [
            {
                title: 'Create Admin',
                description: 'Add a new administrator to the account.',
                onClick: this._showNewAdmin,
                testid: 'create-or-edit-administrators'
            }
        ];

        // Show Plan Sponsor First
        tiles.push({
            testid: "edit-existing-admin-tile",
            title: `${ planSponsor.firstname } ${ planSponsor.lastname }`,
            description: planSponsor.email,
            subtitle: 'Plan Sponsor',
            icon: 'Plan Sponsor',
            status: statusLabel[planSponsor.status] || null,
            onClick: () => this._showEdit(planSponsor)
        });

        Object.keys(this.props.administrators).map((adminID) => {
            if (adminID === planSponsorID) {
                return null;
            }

            const admin = administrators[adminID];

            tiles.push({
                title: `${ admin.firstname } ${ admin.lastname }`,
                description: admin.email,
                subtitle: admin.isParent === 1 ? 'Parent Admin' : null,
                icon: admin.isParent === 1 ? 'Parent Admin' : 'Person',
                status: statusLabel[admin.status] || null,
                onClick: () => this._showEdit(admin)
            });

            return null;
        });

        return tiles;
    };

    render() {
        const styles = {
            container: {
                margin: '10px 0'
            }
        };

        const { divisions } = this.props;
        const writeAccess = permissionLevel('Administrators', this.props.accessType).includes('write');
        const tiles = this._getTiles();
        const removeButton = Number(this.state.isPlanSponsor) === 0 && this.state.isEdit ? 
            {
                label: "Remove",
                onClick: writeAccess ? (Number(this.state.isPlanSponsor) === 0 && this.state.isEdit ? this._removeAdmin : false) : false
            }
            : 
            null;

        return (
            <React.Fragment>
                <Tiles tiles={tiles} />

                <Modal
                    testid="add-edit-admin-modal"
                    open={this.state.showAdminForm}
                    onClose={this._hideForm}
                    title={this.state.isEdit ? 'Edit Admin' : 'Add Admin'}
                    primaryButton={{
                        onClick: writeAccess ? (this.state.isEdit ? this._editAdmin : this._saveAdmin) : false
                    }}
                    secondaryButton={removeButton}
                >

                    <TextInput
                        label="Admin Firstname"
                        value={this.state.firstname}
                        onBlur={() => this._onBlur('firstname')}
                        onChange={(value) => this._handleInputChange('firstname', value)}
                        error={Boolean(this.state.firstnameError)}
                        helperText={this.state.firstnameError}
                        fullWidth
                        autoFocus
                    />

                    <TextInput
                        label="Admin Lastname"
                        value={this.state.lastname}
                        onBlur={() => this._onBlur('lastname')}
                        onChange={(value) => this._handleInputChange('lastname', value)}
                        error={Boolean(this.state.lastnameError)}
                        helperText={this.state.lastnameError}
                        fullWidth
                    />

                    <TextInput
                        label="Admin Email"
                        value={this.state.email}
                        onBlur={() => this._onBlur('email')}
                        onChange={(value) => this._handleInputChange('email', value)}
                        error={Boolean(this.state.emailError)}
                        helperText={this.state.emailError}
                        fullWidth
                    />

                    <TextInput
                        label={ "Language" }
                        value={ this.state.language }
                        onBlur={() => this._onBlur('language')}
                        onChange={(value) => this._handleInputChange('language', value)}
                        select
                        selectOptions={ Services.selectFieldOptions.languageOptions() }
                        fullWidth
                    />

                    <FormControl component="fieldset">
                        <Text
                            medium
                            light
                            margin="20px 0 0px 0"
                        >
                            Enforce Multi-factor Authentication (MFA)
                        </Text>

                        <Text
                            tiny
                            light
                            margin="5px 0 5px 0"
                        >
                            Enforce multi-factor authentication when attempting login.
                        </Text>

                        <BubbleSelectBlock
                            disabled={ this.props.currentAccount.meta.enforceMFA }
                            first={{
                                title: 'Yes',
                                selected: this.props.currentAccount.meta.enforceMFA || Boolean(this.state.useMFA) === true,
                                field: 'enforceMFAYes'
                            }}
                            second={{
                                title: 'No',
                                selected: !this.props.currentAccount.meta.enforceMFA && Boolean(this.state.useMFA) === false,
                                field: 'enforceMFANo'
                            }}
                            onBlur={(e) => this._onBlur('useMFA', e)}
                            handleChange={ async (field) => {
                                if (field === 'enforceMFAYes') {
                                    await this._handleInputChange('useMFA', true, true);
                                } else {
                                    await this._handleInputChange('useMFA', false, true);
                                }
                            }}
                        />

                        {
                            Boolean(this.props.currentAccount.meta.enforceMFA) &&
                                <Text
                                    tiny
                                    light
                                    margin="5px 0 5px 0"
                                >
                                    Multi-factor authentication has been enforced by a group administrator.
                                </Text>
                        }
                    </FormControl>

                    <PermissionsComponent
                        permission="Administrators"
                        accessType={this.props.accessType}
                        level="write"
                    >
                        {
                            this.state.isPlanSponsor === 0 &&
                            <>
                                <Text mediumLarge bold margin="20px 0 10px 0">
                                    Admin Permissions
                                </Text>

                                <>
                                    <Text small>
                                        Give this admin <b>Parent Admin</b> permissions? Parent Admins have full access to the account and can view and manage members under all divisions.
                                    </Text>

                                    <BubbleSelectBlock
                                        first={{
                                            title: 'Yes',
                                            selected: this.state.isParent === 1,
                                            field: 'Yes'
                                        }}
                                        second={{
                                            title: 'No',
                                            selected: this.state.isParent === 0,
                                            field: 'No'
                                        }}
                                        handleChange={(field) => {
                                            if (field === 'Yes') {
                                                this.setState({ isParent: 1 });
                                            } else {
                                                this.setState({ isParent: 0 });
                                            }
                                        }}
                                    />
                                </>

                                {
                                    Number(this.state.isParent) === 0 &&
                                        <React.Fragment>
                                            <Text
                                                small
                                                display="block"
                                                margin="10px 0 10px 0"
                                            >
                                                { `For each division listed below choose which permission level ${ this.state.isEdit ? this.state.firstname : 'the admin' } should have: ` }
                                            </Text>

                                            <div style={ styles.container }>
                                                <Text small bold>None Selected</Text>
                                                <Text tiny margin="5px 0">
                                                    Administrator will not be able to view or manage members under the selected division(s).
                                                </Text>
                                            </div>

                                            <div style={ styles.container }>
                                                <Text small bold>Read Only</Text>
                                                <Text tiny margin="5px 0">
                                                    Administrator can only view members under the selected division(s) but not make changes.
                                                </Text>
                                            </div>

                                            <div style={ styles.container }>
                                                <Text small bold>Read & Write</Text>
                                                <Text tiny margin="5px 0">
                                                    Administrator can view and manage members under the selected division(s).
                                                </Text>
                                            </div>

                                            <Text mediumLarge bold margin="10px 0 10px 0">
                                                Divisions
                                            </Text>

                                            <div style={styles.list}>
                                                {
                                                    helpers.sortObjByTitle(divisions).map((divisionID) => {
                                                        const division = divisions[divisionID];

                                                        return (
                                                            <div key={divisionID} style={styles.listItem}>
                                                                <Text avMedium>{ division.title }</Text>
                                                                <BubbleSelectBlock
                                                                    allowToggleOff={true}
                                                                    first={{
                                                                        title: 'Read Only',
                                                                        selected: this.state.divisionsById[divisionID]?.access === 'Read',
                                                                        field: 'Read'
                                                                    }}
                                                                    second={{
                                                                        title: 'Read & Write',
                                                                        selected: this.state.divisionsById[divisionID]?.access === 'Write',
                                                                        field: 'Write'
                                                                    }}
                                                                    handleChange={ (access) => {
                                                                        this._handleDivisionSelection(divisionID, access);
                                                                    }}
                                                                />
                                                            </div>
                                                        );
                                                    })
                                                }
                                            </div>
                                        </React.Fragment>
                                }
                            </>
                        }

                        {
                            this.state.isEdit &&
                                <Button
                                    onClick={() => this._toggle('resetAdminPasswordModal')}
                                    transparent
                                    marginTop={2.5}
                                >
                                    Send Reset Password Email
                                </Button>
                        }
                    </PermissionsComponent>
                </Modal>

                <Modal
                    open={this.state.resetAdminPasswordModal}
                    onClose={() => this._toggle('resetAdminPasswordModal')}
                    title="Reset Password"
                    primaryButton={{
                        label: "Continue",
                        onClick: this._resetAdminPassword
                    }}
                >
                    <Text medium light>
                        You are about to send an email with a forgotten password link, are you sure you want to continue? This cannot be undone.
                    </Text>
                </Modal>

            </React.Fragment>
        );
    }
}

Administrators.propTypes = {
    administrators: PropTypes.oneOfType([ PropTypes.array, PropTypes.object ]),
    divisions: PropTypes.object,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    administrators: state.accounts.currentAccount.admins,
    currentAccount: state.accounts.currentAccount,
    divisions: state.accounts.currentAccount.divisions,
    accessType: state.master.access
});

export default connect(mapStateToProps)(Administrators);